import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import useAppLayout from './hooks/useAppLayout';
import API_URL from './api/config';
import './styles/style.css'; // Custom CSS for slider
import { toast, ToastContainer } from 'react-toastify';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import ReactModal from 'react-modal';
import imageCompression from 'browser-image-compression';


function ProfileSettings() {

    const { menu, toggleMobileMenu } = useAppLayout();

    const queryClient = useQueryClient();

    const [message, setMessage] = useState('');
    const [activeTab, setActiveTab] = useState("profile");
    
    const [profile, setProfile] = useState({
        name: '',
        last_name: '',
        surname: '',
        n_prefix: '',
        age_set: '',
        date_of_birth: '',
        gender: '',
        phone_number: '',
        phone_number_2: '',
        email: '',
        status: '',
        hometown: '',
        place_of_birth: '',
        religion: '',
        country_id: '',
        facebook: '',
        twitter: '',
        instagram: '',
        linkin: '',
        youtube: '',
        profile_image: ''
        });


    const { data, isLoading, isError } = useQuery({
        queryKey: ['userProfileDetails'],
        queryFn: async () => {
          const res = await axios.get(`${API_URL}/profile-settings-api`, { withCredentials: true });
          return res.data;
        },
        staleTime: 0,
        cacheTime: Infinity
      });
      


     useEffect(() => {
        if (data) {
          if (data.Status === "Success") {
            const user = data.user;
      
            setProfile({
                name: user.name || '',
        last_name: user.last_name || '',
        surname: user.surname || '',
        n_prefix: user.n_prefix || '',
        age_set: user.age_set || '',
        date_of_birth: user.date_of_birth ? user.date_of_birth.substring(0, 10) : '',
        gender: user.gender || '',
        phone_number: user.phone_number || '',
        email: user.email || '',
        status: user.status || '',
        hometown: user.hometown || '',
        place_of_birth: user.place_of_birth || '',
        religion: user.religion || '',
        country_id: user.country_id || '',
        facebook: user.facebook || '',
        twitter: user.twitter || '',
        instagram: user.instagram || '',
        linkin: user.linkin || '',
        youtube: user.youtube || '',
        profile_image: user.profile_image || '',
            });
          } else {
            setMessage("Failed to load user data");
          }
        }
      }, [data]);


    // Handle input changes
    const handleChange = (e) => {
        const { name, value } = e.target;
        setProfile((prev) => ({
            ...prev,
            [name]: value
        }));
    };


    
const [previewImage, setPreviewImage] = useState(null);

const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
  
    const targetSizeKB = 100;
    let compressedFile = file;
    let quality = 0.9;
  
    try {
      // Try compressing with decreasing quality until under 100KB
      while (true) {
        const options = {
          maxWidthOrHeight: 800,
          useWebWorker: true,
          initialQuality: quality
        };
  
        compressedFile = await imageCompression(file, options);
  
        const sizeInKB = compressedFile.size / 1024;
        if (sizeInKB <= targetSizeKB || quality <= 0.1) {
          break; // Done compressing
        }
  
        quality -= 0.1; // Try lower quality
      }
  
      // Preview compressed image
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(compressedFile);
  
      // Set compressed file to profile
      setProfile((prev) => ({
        ...prev,
        profile_image: compressedFile,
      }));
  
    } catch (err) {
      console.error('Image compression error:', err);
      toast.error('Failed to compress image.');
    }
  };


  const [loading, setLoading] = useState(false);

  const handleProfileUpdate = async () => {
    setLoading(true); // Disable the button
  
    const formData = new FormData();
  
    if (profile.profile_image) {
      formData.append('image_name', profile.profile_image);
    }
  
    Object.keys(profile).forEach((key) => {
      if (key !== 'profile_image') {
        formData.append(key, profile[key]);
      }
    });
  
    try {
      const response = await axios.put(`${API_URL}/profile-settings-update-api`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      toast.success("Details updated successfully");
    } catch (error) {
      toast.error("Error updating profile");
      console.error('Error updating profile:', error);
    } finally {
      setLoading(false); // Re-enable the button
    }
  };
  

    

    const handleSocialsUpdate = async (e) => {
        e.preventDefault(); // prevent default form submit behavior
    
        const data = {
            facebook: profile.facebook,
            twitter: profile.twitter,
            instagram: profile.instagram,
            linkin: profile.linkin,
            youtube: profile.youtube,
        };
    
        try {
            const response = await axios.put(`${API_URL}/profile-settings-update-socials-api`, data, {
                headers: {
                    'Content-Type': 'application/json',
                }
            });
            toast.success("Socials updated successfully");
        } catch (error) {
            toast.error("Error updating socials");
            console.error('Error updating socials:', error);
        }
    };
    

     const [changePasswordModalOpen, setChangePasswordModalOpen] = useState(false);

     const [passwordData, setPasswordData] = useState({
        old_password: '',
        new_password: '',
        confirm_new_password: ''
    });
    
    const handlePasswordChange = (e) => {
        setPasswordData({
            ...passwordData,
            [e.target.name]: e.target.value
        });
    };
    
    const handlePasswordUpdate = async (e) => {
        e.preventDefault();
    
        if (!passwordData.old_password || !passwordData.new_password || !passwordData.confirm_new_password) {
            toast.error("All fields are required");
            return;
        }
    
        try {
            const response = await axios.put(`${API_URL}/profile-settings-update-password-api`, passwordData, {
                headers: {
                    'Content-Type': 'application/json',
                }
            });
    
            toast.success("Password updated successfully");
            setChangePasswordModalOpen(false);
        } catch (error) {
            if (error.response && error.response.data && error.response.data.message) {
                toast.error(error.response.data.message);
            } else {
                toast.error("Error updating password");
            }
            console.error('Error updating password:', error);
        }
    };




    const {
      data: lastLoginData,
      isLoading: isLoadinglastLogin,
      isError: isErrorlastLogin
    } = useQuery({
      queryKey: ['lastLogin'],
      queryFn: async () => {
        const res = await axios.get(
          `${API_URL}/get-last-login-api`,
          { withCredentials: true }
        );
        return res.data;
      }
    });
    

    const formatLastLogin = (dateString) => {
      if (!dateString) return "Never logged in";
    
      const date = new Date(dateString);
      const now = new Date();
    
      const isToday = date.toDateString() === now.toDateString();
    
      const yesterday = new Date();
      yesterday.setDate(now.getDate() - 1);
      const isYesterday = date.toDateString() === yesterday.toDateString();
    
      const time = date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      });
    
      if (isToday) return `Today, ${time}`;
      if (isYesterday) return `Yesterday, ${time}`;
    
      return `${date.toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      })}, ${time}`;
    };


const [settings, setSettings] = useState([]);

const { data: notificationSettings, isLoading: isLoadingN, isError: isErrorN } = useQuery({
    queryKey: ['userNotificationSettings'],
    queryFn: async () => {
      const res = await axios.get(`${API_URL}/profile-settings-notifications-api`, { withCredentials: true });
      return res.data;
    },
    
  });

  const handleToggle = (abbrev, currentStatus) => {
    const newStatus = currentStatus === 1 ? 0 : 1;
  
    axios.post(`${API_URL}/profile-settings-update-notifications-api`, {
      notification_name: abbrev, // use abbrev consistently
      status: newStatus,
    }, { withCredentials: true })
      .then(() => {
        toast.success(`Notification turned ${newStatus === 1 ? 'on' : 'off'}.`);
        queryClient.invalidateQueries(['userNotificationSettings']);
      })
      .catch((err) => {
        toast.error('Failed to update setting.');
        console.error(err);
      });
  };
  

  const TabButton = ({ id, label, icon, isActive, onClick }) => (
    <button
      type="button"
      className={`nav-link d-flex align-items-center gap-2 px-4 py-3 border-0 rounded-3 fw-medium transition-all ${
        isActive 
          ? 'bg-primary text-white shadow-sm' 
          : 'bg-light text-muted hover-bg-primary hover-text-white'
      }`}
      onClick={() => onClick(id)}
      style={{ 
        transition: 'all 0.2s ease-in-out',
        minWidth: '140px'
      }}
    >
      <i className={`${icon} ${isActive ? '' : 'text-muted'}`}></i>
      {label}
    </button>
  );



    return (
         
        <div className={`main-wrapper ${menu ? 'slide-nav': ''}`}>
        <Header onMenuClick={(value) => toggleMobileMenu()} />
        <Sidebar />
        <ToastContainer />

         <div className="page-wrapper">
         <div className="content container-fluid">

               {/* Header Section */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h2 className="mb-1 fw-bold">Profile Settings</h2>
                    <p className="text-muted mb-0">Manage your account preferences and information</p>
                  </div>
                </div>
              </div>
            </div>

            
            {/* Navigation Tabs */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="d-flex gap-2 flex-wrap">
                  <TabButton 
                    id="profile" 
                    label="Profile" 
                    icon="fas fa-user" 
                    isActive={activeTab === 'profile'} 
                    onClick={setActiveTab} 
                  />
                  <TabButton 
                    id="socials" 
                    label="Social Links" 
                    icon="fas fa-share-alt" 
                    isActive={activeTab === 'socials'} 
                    onClick={setActiveTab} 
                  />
                  <TabButton 
                    id="notifications" 
                    label="Notifications" 
                    icon="fas fa-bell" 
                    isActive={activeTab === 'notifications'} 
                    onClick={setActiveTab} 
                  />
                  <TabButton 
                    id="security" 
                    label="Security" 
                    icon="fas fa-shield-alt" 
                    isActive={activeTab === 'security'} 
                    onClick={setActiveTab} 
                  />
                </div>
              </div>
            </div>



             {/* Profile Tab */}
             {activeTab === 'profile' && (
              <div className="row">
                <div className="col-12">
                  <div className="card border-0 shadow-sm rounded-4">
                    <div className="card-body p-4 p-md-5">
                      {/* Profile Header with Avatar */}

                      
                      <div className="text-center mb-5">
                        <div className="position-relative d-inline-block">
                          <div
                            className="position-relative cursor-pointer"
                            onClick={() =>
                              document.getElementById("uploadImageInput")?.click()
                            }
                          >
                            
                            <div
                              className="rounded-circle overflow-hidden border border-4 border-white shadow-lg bg-gradient"
                              style={{ 
                                width: 140, 
                                height: 140,
                                background: 'linear-gradient(135deg, #66ea7eff 0%, #4ba26cff 100%)'
                              }}
                            >
                              {profile.profile_image ? (
                                <img
                                  src={
                                    previewImage ||
                                    `${API_URL}/images/${profile.profile_image}`
                                  }
                                  alt="Profile"
                                  className="w-100 h-100 object-fit-cover"
                                />
                              ) : (
                                <div className="w-100 h-100 d-flex align-items-center justify-content-center text-white">
                                  <i className="fas fa-user fa-3x opacity-75"></i>
                                </div>
                              )}
                            </div>

                            
                            <span className="avatar-camera">
                      <i className="fas fa-camera"></i>
                    </span>

                <style>{`    
                    .avatar-camera{
    position:absolute;right:-2px;bottom:-2px;width:28px;height:28px;
    border-radius:50%;background:var(--brand-dark);color:#184A2C;border:2px solid #fff;
    display:flex;align-items:center;justify-content:center;box-shadow:0 2px 8px rgba(0,0,0,.25)
  } `}</style>
                            

                            <input
                              id="uploadImageInput"
                              type="file"
                              accept="image/*"
                              onChange={handleImageChange}
                              className="d-none"
                            />
                          </div>
                        </div>

                        <h3 className="mt-3 mb-1 fw-bold">
                          {profile.name || profile.last_name
                            ? `${profile.name ?? ""} ${profile.last_name ?? ""}`.trim()
                            : "Your Profile"}
                        </h3>
                        <p className="text-muted">Update your personal information</p>
                      </div>


                      {/* Profile Form */}
                      <div className="row g-4">
                        <div className="col-md-6">
                          <div className="form-floating">
                            <input
                              type="text"
                              name="name"
                              value={profile.name}
                              onChange={handleChange}
                              className="form-control"
                              placeholder="First Name"
                              id="firstName"
                            />
                            <label htmlFor="firstName">
                              <i className="fas fa-user me-2 text-primary"></i>First Name
                            </label>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-floating">
                            <input
                              type="text"
                              name="last_name"
                              value={profile.last_name}
                              onChange={handleChange}
                              className="form-control"
                              placeholder="Last Name"
                              id="lastName"
                            />
                            <label htmlFor="lastName">
                              <i className="fas fa-user me-2 text-primary"></i>Last Name
                            </label>
                          </div>
                        </div>
                        

                        <div className="col-md-6">
                          <div className="form-floating">
                            <input
                              type="text"
                              name="surname"
                              value={profile.surname}
                              onChange={handleChange}
                              className="form-control"
                              placeholder="Surname"
                              id="surname"
                            />
                            <label htmlFor="surname">
                              <i className="fas fa-signature me-2 text-primary"></i>Surname
                            </label>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-floating">
                            <input
                              type="text"
                              name="n_prefix"
                              value={profile.n_prefix}
                              onChange={handleChange}
                              className="form-control"
                              placeholder="Name Prefix"
                              id="prefix"
                            />
                            <label htmlFor="prefix">
                              <i className="fas fa-tag me-2 text-primary"></i>Name Prefix
                            </label>
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="form-floating">
                            <input
                              type="date"
                              name="date_of_birth"
                              value={profile.date_of_birth}
                              onChange={handleChange}
                              className="form-control"
                              id="birthday"
                            />
                            <label htmlFor="birthday">
                              <i className="fas fa-calendar-alt me-2 text-primary"></i>Date of Birth
                            </label>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-floating">
                            <input
                              type="email"
                              name="email"
                              value={profile.email}
                              onChange={handleChange}
                              className="form-control"
                              placeholder="Email"
                              id="email"
                            />
                            <label htmlFor="email">
                              <i className="fas fa-envelope me-2 text-primary"></i>Email Address
                            </label>
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="form-floating">
                            <input
                              type="tel"
                              name="phone_number"
                              value={profile.phone_number}
                              onChange={handleChange}
                              className="form-control"
                              placeholder="Phone Number"
                              id="phone"
                            />
                            <label htmlFor="phone">
                              <i className="fas fa-phone me-2 text-primary"></i>Phone Number
                            </label>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-floating">
                            <input
                              type="text"
                              name="place_of_birth"
                              value={profile.place_of_birth}
                              onChange={handleChange}
                              className="form-control"
                              placeholder="Place of Birth"
                              id="birthplace"
                            />
                            <label htmlFor="birthplace">
                              <i className="fas fa-map-marker-alt me-2 text-primary"></i>Place of Birth
                            </label>
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="form-floating">
                            <input
                              type="text"
                              name="hometown"
                              value={profile.hometown}
                              onChange={handleChange}
                              className="form-control"
                              placeholder="Hometown"
                              id="hometown"
                            />
                            <label htmlFor="hometown">
                              <i className="fas fa-home me-2 text-primary"></i>Hometown
                            </label>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-floating">
                            <input
                              type="text"
                              name="religion"
                              value={profile.religion}
                              onChange={handleChange}
                              className="form-control"
                              placeholder="Religion"
                              id="religion"
                            />
                            <label htmlFor="religion">
                              <i className="fas fa-pray me-2 text-primary"></i>Religious Beliefs
                            </label>
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="form-floating">
                            <input
                              type="text"
                              name="age_set"
                              value={profile.age_set}
                              onChange={handleChange}
                              className="form-control"
                              placeholder="Age Set"
                              id="ageSet"
                            />
                            <label htmlFor="ageSet">
                              <i className="fas fa-users me-2 text-primary"></i>Age Set
                            </label>
                          </div>
                        </div>
                      </div>
<div className="mt-4 pt-3 border-top d-flex justify-content-center">
  <button
    type="button"
    className="btn btn-emerald btn-lgx shadow-green d-inline-flex align-items-center gap-2"
    onClick={handleProfileUpdate}
  >
    <i className="fas fa-save" />
    Update Profile
  </button>
</div>



                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Social Links Tab */}
            {activeTab === 'socials' && (
              <div className="row">
                <div className="col-12">
                  <div className="card border-0 shadow-sm rounded-4">
                    <div className="card-body p-4 p-md-5">
                      <div className="text-center mb-4">
                        <div className="bg-primary rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                             style={{ width: 80, height: 80 }}>
                          <i className="fas fa-share-alt fa-2x text-white"></i>
                        </div>
                        <h3 className="mb-2 fw-bold">Social Media Links</h3>
                        <p className="text-muted">Connect your social media accounts</p>
                      </div>

                      <form onSubmit={handleSocialsUpdate}>
                        <div className="row g-4">
                          <div className="col-md-6">
                            <div className="form-floating">
                              <input
                                type="url"
                                name="facebook"
                                value={profile.facebook}
                                onChange={handleChange}
                                className="form-control"
                                placeholder="Facebook URL"
                                id="facebook"
                              />
                              <label htmlFor="facebook">
                                <i className="fab fa-facebook-f me-2" style={{ color: '#42b25aff' }}></i>
                                Facebook
                              </label>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-floating">
                              <input
                                type="url"
                                name="twitter"
                                value={profile.twitter}
                                onChange={handleChange}
                                className="form-control"
                                placeholder="Twitter URL"
                                id="twitter"
                              />
                              <label htmlFor="twitter">
                                <i className="fab fa-x-twitter me-2" style={{ color: '#000000' }}></i>
                                Twitter (X)
                              </label>
                            </div>
                          </div>

                          <div className="col-md-6">
                            <div className="form-floating">
                              <input
                                type="url"
                                name="instagram"
                                value={profile.instagram}
                                onChange={handleChange}
                                className="form-control"
                                placeholder="Instagram URL"
                                id="instagram"
                              />
                              <label htmlFor="instagram">
                                <i className="fab fa-instagram me-2" style={{ color: '#E4405F' }}></i>
                                Instagram
                              </label>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-floating">
                              <input
                                type="url"
                                name="linkin"
                                value={profile.linkin}
                                onChange={handleChange}
                                className="form-control"
                                placeholder="LinkedIn URL"
                                id="linkedin"
                              />
                              <label htmlFor="linkedin">
                                <i className="fab fa-linkedin-in me-2" style={{ color: '#00b515ff' }}></i>
                                LinkedIn
                              </label>
                            </div>
                          </div>

                          <div className="col-md-6">
                            <div className="form-floating">
                              <input
                                type="url"
                                name="youtube"
                                value={profile.youtube}
                                onChange={handleChange}
                                className="form-control"
                                placeholder="YouTube URL"
                                id="youtube"
                              />
                              <label htmlFor="youtube">
                                <i className="fab fa-youtube me-2" style={{ color: '#FF0000' }}></i>
                                YouTube
                              </label>
                            </div>
                          </div>
                        </div>
<div className="mt-4">
  <hr className="opacity-25 my-0" /> {/* subtle line */}
  <div className="d-flex justify-content-center mt-3">
    <button
      type="submit"
      className="btn btn-emerald btn-lgx shadow-green d-inline-flex align-items-center gap-2"
    >
      <i className="fas fa-save" />
      Save Social Links
    </button>
  </div>
</div>



                      </form>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Notifications Tab */}
            {activeTab === 'notifications' && (
              <div className="row">
                <div className="col-12">
                  <div className="card border-0 shadow-sm rounded-4">
                    <div className="card-body p-4 p-md-5">
                      <div className="text-center mb-4">
                        <div className="bg-primary rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                             style={{ width: 80, height: 80 }}>
                          <i className="fas fa-bell fa-2x text-white"></i>
                        </div>
                        <h3 className="mb-2 fw-bold">Notification Preferences</h3>
                        <p className="text-muted">Customize how you receive updates and alerts</p>
                      </div>

                      {isLoadingN ? (
                        <div className="text-center py-5">
                          <div className="spinner-border text-primary mb-3" role="status"></div>
                          <p className="text-muted">Loading notification settings...</p>
                        </div>
                      ) : isErrorN ? (
                        <div className="alert alert-danger rounded-3" role="alert">
                          <i className="fas fa-exclamation-triangle me-2"></i>
                          Failed to load notification settings. Please try again.
                        </div>
                      ) : notificationSettings && Array.isArray(notificationSettings) ? (
                        <div className="row g-3">
                          {notificationSettings.map((setting) => (
                            <div key={setting.notification_id || setting.abbrev} className="col-12">
                              <div className="d-flex justify-content-between align-items-center p-4 rounded-3 border border-light bg-light-subtle">
                                <div className="flex-grow-1">
                                  <h6 className="mb-1 fw-semibold">{setting.notification_name}</h6>
                                  <p className="mb-0 text-muted small">Receive alerts and updates for this category</p>
                                </div>
                                
                                <div className="form-check form-switch ms-3">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    role="switch"
                                    id={`switch-${setting.abbrev}`}
                                    checked={setting.status === 1}
                                    onChange={() => handleToggle(setting.abbrev, setting.status)}
                                    style={{ fontSize: '1.2rem' }}
                                  />
                                  <label className="form-check-label" htmlFor={`switch-${setting.abbrev}`}>
                                    <span className="visually-hidden">Toggle {setting.notification_name}</span>
                                  </label>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-5">
                          <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
                          <p className="text-muted">No notification settings available</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Security Tab */}
            {activeTab === 'security' && (
              <div className="row">
                <div className="col-12">
                  <div className="card border-0 shadow-sm rounded-4 mb-4">
                    <div className="card-body p-4 p-md-5">
                      <div className="text-center mb-4">
                        <div className="bg-primary rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                             style={{ width: 80, height: 80 }}>
                          <i className="fas fa-shield-alt fa-2x text-white"></i>
                        </div>
                        <h3 className="mb-2 fw-bold">Security Settings</h3>
                        <p className="text-muted">Manage your account security and password</p>
                      </div>

                      <div className="row g-4">
                        <div className="col-md-6">
                          <div className="card border border-primary-subtle bg-primary-subtle h-100">
                            <div className="card-body text-center p-4">
                              <i className="fas fa-key fa-2x text-primary mb-3"></i>
                              <h5 className="fw-semibold mb-2">Change Password</h5>
                              <p className="text-muted mb-3 small">Update your account password for better security</p>
                              <button
  type="button"
  className="change-password-btn"
  onClick={() => setChangePasswordModalOpen(true)}  // 👈 Add this line
  style={{
    background: '#75B437',
    color: 'white',
    border: 'none',
    borderRadius: 12,
    padding: '14px 32px',
    fontSize: 16,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 10,
    boxShadow: '0 4px 12px rgba(117, 180, 55, 0.3)',
  }}
>

  Change Password
</button>
                            </div>
                          </div>
                        </div>
                        
                        <div className="col-md-6">
                          <div className="card border border-info-subtle bg-info-subtle h-100">
                            <div className="card-body text-center p-4">
                              <i className="fas fa-clock fa-2x text-info mb-3"></i>
                              <h5 className="fw-semibold mb-2">Last Login</h5>
                              <p className="text-muted mb-3 small">Monitor your account access activity</p>
                              <p className="fw-medium text-info mb-0">
                                <i className="fas fa-globe me-2"></i>


                                {isLoadinglastLogin && "Loading..."}
                                {isErrorlastLogin && "Failed to load last login"}
                                {!isLoadinglastLogin && !isErrorlastLogin &&
                                  formatLastLogin(lastLoginData?.lastLogin)
                                }


                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Danger Zone */}
                  <div className="card border-danger shadow-sm rounded-4">
                    <div className="card-header bg-danger-subtle border-danger-subtle">
                      <div className="d-flex align-items-center">
                        <i className="fas fa-exclamation-triangle text-danger me-2"></i>
                        <h5 className="mb-0 fw-semibold text-danger">Delete Zone</h5>
                      </div>
                    </div>
                    <div className="card-body p-4">
                      <p className="text-muted mb-4">
                        These actions are irreversible. Please proceed with caution.
                      </p>
                      
                      <div className="row g-3">

                      <div className="col-md-6">
                      <Link to="/deactivate-account" className="btn btn-warning-grad btn-lgx d-block text-center">
  <i className="fas fa-pause-circle me-2" />
  Deactivate Account
</Link>

                        </div>

                        <div className="col-md-6"> 
                        
<Link to="/delete-account" className="btn btn-danger-grad btn-lgx d-block text-center  ">
  <i className="fas fa-trash-alt me-2" />
  Delete Account
</Link>
 
                          <small className="text-muted mt-1 d-block">
                            Permanently delete your account and data
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}


              <ReactModal
              isOpen={changePasswordModalOpen}
              onRequestClose={() => setChangePasswordModalOpen(false)}
              className="Modal"
              overlayClassName="Overlay"
              ariaHideApp={false}
             
            >
                          <div className="modal-content border-0 shadow-lg rounded-4">
                            <div className="modal-header border-0 pb-0">
                              <div className="w-100 text-center">
                                
                                <h5 className="modal-title fw-bold">Change Password</h5>
                                
                              </div>
                              <button
                                type="button"
                                className="btn-close position-absolute top-0 end-0 m-3"
                                onClick={() => setChangePasswordModalOpen(false)}
                              />
                            </div>
                            
                            <div className="modal-body px-4 py-3">
                              <form onSubmit={handlePasswordUpdate}>
                                <div className="row g-3">
                                  <div className="col-12">
                                    <div className="form-floating">
                                      <input
                                        type="password"
                                        name="old_password"
                                        className="form-control"
                                        placeholder="Current password"
                                        value={passwordData.old_password}
                                        onChange={handlePasswordChange}
                                        required
                                        id="currentPassword"
                                      />
                                      <label htmlFor="currentPassword">
                                        <i className="fas fa-lock me-2 text-muted"></i>Current Password
                                      </label>
                                    </div>
                                  </div>
                                  
                                  <div className="col-12">
                                    <div className="form-floating">
                                      <input
                                        type="password"
                                        name="new_password"
                                        className="form-control"
                                        placeholder="New password"
                                        value={passwordData.new_password}
                                        onChange={handlePasswordChange}
                                        required
                                        id="newPassword"
                                      />
                                      <label htmlFor="newPassword">
                                        <i className="fas fa-key me-2 text-muted"></i>New Password
                                      </label>
                                    </div>
                                  </div>
                                  
                                  <div className="col-12">
                                    <div className="form-floating">
                                      <input
                                        type="password"
                                        name="confirm_new_password"
                                        className={`form-control ${
                                          passwordData.confirm_new_password && 
                                          passwordData.new_password !== passwordData.confirm_new_password 
                                            ? 'is-invalid' : ''
                                        }`}
                                        placeholder="Confirm new password"
                                        value={passwordData.confirm_new_password}
                                        onChange={handlePasswordChange}
                                        required
                                        id="confirmPassword"
                                      />
                                      <label htmlFor="confirmPassword">
                                        <i className="fas fa-check me-2 text-muted"></i>Confirm New Password
                                      </label>
                                      {passwordData.confirm_new_password && 
                                       passwordData.new_password !== passwordData.confirm_new_password && (
                                        <div className="invalid-feedback">
                                          Passwords do not match
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
            
                                <div className="mt-4 pt-3 border-top">
                                  <div className="d-flex gap-2">
                                 <button 
              type="button" 
              className="btn btn-cancel btn-lgx flex-fill"
              onClick={() => setChangePasswordModalOpen(false)}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-emerald btn-lgx shadow-green flex-fill">
              
             Update
            </button>
            
                                    
                                  </div>
                                </div>
                              </form>
                            </div>
                          </div>
                        </ReactModal>



        </div>
        </div>
        
        </div>
       
    );
}

export default ProfileSettings;
